import React, { useEffect, useState, useCallback } from 'react';

import { useHistory } from 'react-router-dom';
import {
  getPayloadAttestText,
  handleSignatureInitiation,
} from '../customerRenewalsUtils';
import { PubSub } from '../../../../utils/eventUtils';
import { verifyOrgProfileAll } from '../../../../accounts/AccountService';
import { manageAPIError } from '../../../../utils';
import _ from 'lodash';
import useNotifications from '../../../../_lib/notifications/useNotifications';

export const useQuoteAttestation = ({
  quote = {},
  onAttest,
  onAttestCompleted = _.noop,
  onAttestFailed = handleAttestFailed,
  onChangePayload,
  onSignatureInitiatation,
  onInvalidRoute = handleInvalidRoute,
}: any) => {
  const notifications = useNotifications();
  const { push } = useHistory();

  const [invalidQuote, setInvalidQuote] = useState(false);
  const [attestments, setAttestments] = React.useState({
    noLossAttested: false,
    attesting: false,
  });

  const {
    noKnownLossLetterReq,
    customerStatus,
    effectiveDate,
    created,
    quoteNumber,
    product,
    id,
  } = quote;

  const NoKnownLossIsReq = noKnownLossLetterReq !== 'NOT_REQUIRED';
  const text = getPayloadAttestText(effectiveDate, created, product);

  useEffect(() => {
    if (!quote) {
      return;
    }

    if (quoteInvalidStatus.includes(customerStatus)) {
      setInvalidQuote(true);
    }

    setAttestments((current) => ({
      ...current,
      noLossAttested: lossLetterMappings[noKnownLossLetterReq],
    }));
    // eslint-disable-next-line
  }, [quote]);

  useEffect(() => {
    const invQ = PubSub.subscribe('quote-invalidated', (value: any) => {
      setInvalidQuote(value);
    });

    return () => {
      invQ.remove();
    };
    // eslint-disable-next-line
  }, []);

  const setNoLossAttested = useCallback((newBoolean: any) => {
    setAttestments((current) => ({
      ...current,
      noLossAttested: newBoolean,
    }));
  }, []);

  const handleAttest = useCallback(() => {
    if (invalidQuote) {
      onInvalidRoute(push);
    } else {
      setAttestments((current) => ({
        ...current,
        attesting: true,
      }));

      let payload = {
        quoteId: id,
        quoteNumber,
        isRequired: attestments.noLossAttested,
        product,
        attestedText: NoKnownLossIsReq ? '' : text,
      };

      if (typeof onChangePayload === 'function') {
        payload = onChangePayload(payload);
      }
      if (typeof onAttest === 'function') {
        onAttest(payload);
      }

      verifyOrgProfileAll(payload)
        .then(({ data }) =>
          setTimeout(() => {
            onAttestCompleted(data);
            if (typeof onSignatureInitiatation === 'function') {
              onSignatureInitiatation(quote);
            } else {
              handleSignatureInitiation(quote, push);
            }
          }, 1000)
        )
        .catch((error) => onAttestFailed(error, notifications))
        .finally(() =>
          setAttestments((current) => ({
            ...current,
            attesting: false,
          }))
        );
    }
    // eslint-disable-next-line
  }, []);

  return {
    invalidQuote,
    NoKnownLossIsReq,
    setNoLossAttested,
    handleAttest,
    handleInvalidRoute,
    handleSignatureInitiation,
    ...attestments,
  };
};

const lossLetterMappings = {
  PROVIDED: true,
  NOT_REQUIRED: false,
  REQUIRED: false,
};

const quoteInvalidStatus = ['INVALID', 'REFERRED'];

function handleAttestFailed(error: any, notifications: any) {
  return notifications.error(
    manageAPIError(
      error,
      'Something went wrong while verifying the organization profile.'
    )
  );
}

function handleInvalidRoute(push: any) {
  setTimeout(() => {
    push('/customer/policies');
  }, 2000);
}
